import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ComponentDefinition, TagDefinitionDTO, QualityId, DefaultGridsterOptions, ConnectionPoint, TagDefinitionWithErrorsDTO, TagDefinition, RefreshClickData } from '../../../models';
import { IinputValuesStatusService, IVariableCascadingService, IExportToCsvService, IUserSettingsService, IAuthorizationService, IDateRangeService, IGroupService, IConnectionPointService, IStoreService } from '../../../services';
import { defaultItemPerPage, momentFormat, refreshClick, tagDefaultState } from '../../../common';
import { manualInputRoles } from '../../../common/Roles';

class TagsInErrorStateController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    static componentId = 'TagsInErrorController';
    static menuConfig = `${TagsInErrorStateController.componentId}.menu`;

    mapped: TagDefinitionDTO[];
    tagsFiltered: TagDefinitionDTO[];
    tagsInError: TagDefinitionDTO[];
    selectedRowIndex: number;
    variables: any[];
    format: any;
    searchEnable = {
        tag: true,
        err: true,
        comment: true,
        unit: true,
        variable: true,
        source: true,
        group: true,
        identifier: true
    };
    initialRange: any;
    displayed: any[];
    hasManualInputRole: boolean;
    currentInterval: any;
    hasMore: boolean;
    hasLess: boolean;
    range: any;
    formattedRange: { beginDate: string; endDate: string };
    errorStatus = QualityId;
    gridsterOptions = DefaultGridsterOptions;
    menu = {
        errors: { show: true, label: 'Data quality' },
        type: { show: false, label: 'Data type' }
    };
    searchString: string;
    searchFormat = {
        name: f => f.name,
        comment: f => f.shortComment,
        variable: f => f.variableName ? f.variableName : '',
        unit: f => f.unitName ? f.unitName : '',
        source: f => f.connectionPointName ? f.connectionPointName : '',
        tag: f => f.name,
        identifier: f => f.identifierNames.join(','),
        group: f => f.groups.map(g => g.absoluteName).join(','),
        error: f => {
            let ret = '';
            for (const prop of Object.keys(f.errors)) {
                if (f.errors[prop] > 0) {
                    ret += this.errorStatus[prop];
                }
            }
            return ret;
        },
        order: (regExp, filteredTags) => {
            for (const status of Object.keys(this.errorStatus)) {
                if (regExp.test(status.toLowerCase())) {
                    return _.sortBy(filteredTags, f => (f as any).errors[this.errorStatus[status]], ['desc'])
                        .reverse();
                }
            }
            return filteredTags;
        }
    };

    private _pageSize = defaultItemPerPage;
    private _pageNumber = 1;
    private _connectionPoints: ConnectionPoint[];

    static $inject = [
        '$scope',
        '$state',
        'storeService',
        'inputValuesStatusService',
        'variableCascadingService', 'exportToCsvService',
        'userSettingsService',
        'authorizationService',
        'dateRangeService',
        'groupService',
        'connectionPointService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$state: angular.ui.IStateService,
        private _storeService: IStoreService,
        private _inputValueStatusService: IinputValuesStatusService,
        private _variableCascadingService: IVariableCascadingService,
        private _exportToCsvService: IExportToCsvService,
        private _userSettingsService: IUserSettingsService,
        private _authorizationService: IAuthorizationService,
        private _dateRangeService: IDateRangeService,
        private _groupService: IGroupService,
        private _connectionPointService: IConnectionPointService
    ) {
    }

    $onInit() {
        if (this._$state.params['selectedGroup']) {
            this.searchString = `g:${this._$state.params['selectedGroup'].uniqueName}`;
            this._$state.params['selectedGroup'] = null;
        } else if (this._$state.params['groupGuid']) {
            this._groupService.getByGuid(this._$state.params['groupGuid'])
                .then(dto => {
                    this.searchString = `g:${dto.group.uniqueName}`;
                });
        }
        const dates = this._dateRangeService.initByState(this._$state.params);
        if (dates.beginDate && dates.endDate) {
            this.initialRange = {
                beginDate: moment(dates.beginDate).format(momentFormat),
                endDate: moment(dates.endDate).format(momentFormat)
            };
        }
        this.hasManualInputRole = this._authorizationService.isInRoles(manualInputRoles);

        this.tagsInError = [];

        this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
            this.isLoading = true;
            this.loadData(data.force);
        });

        if (this._$state.params['errorSearch']) {
            this.searchString = `err:${this._$state.params['errorSearch']}`;
        }

        this._$scope.$watch(() => this.tagsFiltered,
            (newValue: TagDefinitionWithErrorsDTO[], oldValue: any) => {
                if (newValue) {

                    this.updateDisplayed();
                }
            });

        this._$scope.$watch(() => this.range, () => {
            if (this.range) {
                this.formattedRange = {
                    beginDate: moment(this.range.beginDate).format('YYYY-MM-DD HH:mm'),
                    endDate: moment(this.range.endDate).format('YYYY-MM-DD HH:mm')
                };
                this.currentInterval = {
                    startDate: this.formattedRange.beginDate,
                    endDate: this.formattedRange.endDate
                };
            }
        }, true);


        this.loadMenu();
        this._$scope.$watch(() => this.menu, () => {
            this._userSettingsService.setApplicationSetting(TagsInErrorStateController.menuConfig,
                JSON.stringify(this.menu));
        }, true);
        this.loadData();
    }

    private loadMenu() {
        const conf = this._userSettingsService.getApplicationSetting(TagsInErrorStateController.menuConfig);
        try {
            const obj = JSON.parse(conf as string);
            Object.keys(this.menu).forEach(k => {
                if (obj[k]) {
                    this.menu[k].show = obj[k].show;
                }
            });
        } catch (e) {
            // silent drop
        }
    }

    exportToCsv() {
        const fileName = `TagsInsError_${moment(new Date()).format('YYYY-MM-DD')}.csv`;
        const data = [this.getCsvHeader()];

        this.tagsFiltered.forEach(r => data.push(this.getCsvRow(r)));

        this._exportToCsvService.getCsvFromData(data, fileName);
    }

    private getCsvHeader() {
        const header: string[] = [];

        header.push('Name');
        header.push('Short comment');
        if (this.menu.errors.show) {
            header.push('% Missing');
            header.push('% Bad');
            header.push('% Flat');
            header.push('% Out of range');
            header.push('% Scatter');
            header.push('Requires review');
        }

        if (this.menu.type.show) {
            header.push('Type');
            header.push('Subtype');
            header.push('Qualifier');
            header.push('Unit');
        }
        header.push('Ignored');

        return header;
    }

    private getCsvRow(tag): string[] {
        const row: string[] = [];

        row.push(tag.name);
        row.push(tag.shortComment);
        if (this.menu.errors.show) {
            row.push(this.number(tag, this.errorStatus.Missing));
            row.push(this.number(tag, this.errorStatus.Bad));
            row.push(this.number(tag, this.errorStatus.Flat));
            row.push(this.number(tag, this.errorStatus.OutOfRange));
            row.push(this.number(tag, this.errorStatus.Scatter));
            row.push(tag.isReviewRequired);
        }

        if (this.menu.type.show) {
            row.push(tag.variableName);
            row.push(tag.variableSubTypeName);
            row.push(tag.qualifierName);
            row.push(tag.unitName);
        }
        row.push(tag.isIgnored);

        return row;
    }

    ignore(tagDefinition: TagDefinition, $event): void {
        this.isLoading = true;
        tagDefinition.isIgnored = true;
        //this._tagDefinitionService.update([tagDefinition])
        this._storeService.dispatchers().tags.update([tagDefinition])
            .then(() => {
                const index = _.findIndex(this.tagsInError, t => t.tagDefinition.guid === tagDefinition.guid);
                if (index > -1) {
                    this.tagsInError.splice(index, 1);
                    this.mapped = this.map(this.tagsInError);
                }
            })
            .finally(() => this.isLoading = false);

        $event.preventDefault();
    }

    setClickedRow(index: number): void {
        this.selectedRowIndex = index;
    }

    selectItem(item, $event) {
        if (item) {
            this._$state.go(tagDefaultState, { tagDefinitionGuid: item.guid, tag: item });
            $event.preventDefault();
        }
    }

    getVariableSubTypeName(tag: TagDefinition) {
        return this._variableCascadingService.getVariableSubTypeName(tag.variableTypeGuid, tag.variableSubtypeGuid);
    }

    getVariableUnitName(tag: TagDefinition) {
        return this._variableCascadingService.getVariableUnitName(tag.variableTypeGuid, tag.unitGuid);
    }


    getVariableQualifierName(tag: TagDefinition) {
        return this._variableCascadingService.getVariableQualifierName(tag.variableTypeGuid, tag.qualifierGuid);
    }

    getVariableName(tag: TagDefinition) {
        return this._variableCascadingService.getVariableName(tag.variableTypeGuid);
    }


    getConnectionPointName(tag: TagDefinition): string {
        const connectionPoint = _.find(this._connectionPoints, c => c.guid === tag.acquisitionPointGuid);
        return connectionPoint ? connectionPoint.name : '';
    }

    isWarn(tag, value: QualityId): boolean {
        const v = tag.errors[value] * 100;
        return (value) ? v >= 5 && v < 20 : false;
    }

    isError(tag, value: QualityId): boolean {
        const v = tag.errors[value];
        return v ? (v * 100) >= 20 : false;
    }

    showTags(mode: string): void {
        this._$state.go('tags', { mode: mode });
    }

    more() {
        this._pageNumber++;
        this.updateDisplayed();
    }

    less() {
        this._pageNumber--;
        this.updateDisplayed();
    }

    number(tag, status: number): string {
        let percent = tag.errors[status];
        if (percent) {
            percent *= 100;
            return (percent < 0.5) ? '>0' : percent.toFixed(0) + '%';
        }
    }

    private updateDisplayed() {
        const filtered = this.tagsFiltered;
        this.displayed = _.take(filtered, this._pageNumber * this._pageSize);
        this.hasMore = filtered && this.tagsFiltered && this.displayed.length < filtered.length;
        this.hasLess = this.displayed.length > this._pageSize;
    }

    private loadData(force: boolean = false) {
        this._connectionPointService.getAll()
            .then(dtos => {
                this._connectionPoints = dtos.map(dto => dto.connectionPoint);
                return this._inputValueStatusService.getPercentageBadValuesPerTag(force);
            })
            .then(data => {
                this._pageNumber = 1;
                this.tagsInError = data;
                this.mapped = this.map(data);
            })
            .finally(() => this.isLoading = false);
    }
    private map(data: any[]): any[] {
        return data.map(d => {
            const result = d.tagDefinition;
            result.errors = d.errors;
            result.variableName = this.getVariableName(d.tagDefinition);
            result.variableSubTypeName = this.getVariableSubTypeName(d.tagDefinition);
            result.qualifierName = this.getVariableQualifierName(d.tagDefinition);
            result.unitName = this.getVariableUnitName(d.tagDefinition);
            result.groups = d.groups;
            result.connectionPointName = this.getConnectionPointName(d.tagDefinition);
            result.identifierNames = d.identifierNames;
            result.displayComment = result.comment
                ? result.comment
                : result.shortComment
                    ? result.shortComment
                    : result.longComment.substring(0, 16);
            return result;
        });
    }
}

export const wtTagsInErrorState: ComponentDefinition = {
    componentId: 'wtTagsInErrorState',
    controller: TagsInErrorStateController,
    template: require('./tagsInErrorState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};